import { useState } from "react";
import { motion } from "framer-motion";
import { api, getErrorMessage } from "@/lib/seoApi";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Link2, Unlink, Network, Search, AlertTriangle, CheckCircle2, ArrowRight, ExternalLink,
} from "lucide-react";

const T = {
  en: {
    title: "Link Tools",
    sub: "White-hat backlink and internal-linking tools. Find broken links to reclaim, audit any page's link profile, and discover internal-linking opportunities on your own site.",
    tabBroken: "Broken Link Finder",
    tabInternal: "Internal Links",
    tabAudit: "Link Auditor",
    // Broken
    brokenSub: "Enter any page (yours or a competitor's resource page). We test every outbound link and list the dead ones - the foundation of broken-link building: offer your content as the replacement.",
    urlPh: "example.com/resources",
    scan: "Find broken links",
    scanning: "Checking links...",
    brokenLoading: "Testing every outbound link...",
    totalLinks: "Total links",
    checkedLinks: "Checked",
    brokenLinks: "Broken",
    noBroken: "No broken links found on this page. Clean!",
    truncated: (n) => `Only the first ${n} links were checked.`,
    thStatus: "Status",
    thType: "Type",
    thUrl: "Broken URL",
    thAnchor: "Anchor text",
    internalType: "internal",
    externalType: "external",
    // Internal
    internalSub: "Enter your own domain. We crawl up to 20 pages, find orphan pages (no internal links pointing to them) and suggest internal links between related pages - both help search and AI engines understand your site.",
    yourDomain: "yourwebsite.com",
    analyze: "Analyze internal links",
    analyzing: "Crawling your site...",
    internalLoading: "Crawling up to 20 pages and mapping internal links...",
    pagesCrawled: "Pages crawled",
    orphans: "Orphan pages",
    thinPages: "Thinly-linked",
    orphansTitle: "Orphan pages (no internal links point here)",
    orphansDesc: "These pages are hard for crawlers and users to discover. Add internal links to them from related pages.",
    thinTitle: "Thinly-linked pages (only 1 internal link)",
    suggestionsTitle: "Suggested internal links",
    suggestionsDesc: "Related pages that don't link to each other yet. Add a contextual link from the source to the target.",
    from: "From",
    to: "To",
    sharedTopics: "Shared topics",
    noneInternal: "No orphan or thinly-linked pages found - your internal linking looks healthy.",
    mostLinked: "Most internally-linked pages",
    inbound: "inbound links",
    // Audit
    auditSub: "Enter any URL for an instant breakdown of its links: internal vs external, dofollow vs nofollow, anchor-text quality and the domains it links out to.",
    audit: "Audit links",
    auditing: "Analyzing...",
    total: "Total links",
    internal: "Internal",
    external: "External",
    dofollow: "Dofollow",
    nofollow: "Nofollow",
    emptyAnchors: "Empty anchors",
    genericAnchors: "Generic anchors",
    anchorWarn: "Generic anchors like \"click here\" pass less context to search and AI engines. Use descriptive anchor text.",
    topDomains: "Top external domains linked",
    topAnchors: "Most common anchor texts",
    thDomain: "Domain",
    thCount: "Links",
    thAnchorText: "Anchor",
    externalList: "External links",
    errTitle: "Could not analyze",
    errEmpty: "Please enter a URL",
  },
  fr: {
    title: "Outils de liens",
    sub: "Outils white-hat de backlinks et de maillage interne. Trouvez les liens cassés à récupérer, auditez le profil de liens d'une page, et découvrez des opportunités de maillage interne sur votre site.",
    tabBroken: "Liens cassés",
    tabInternal: "Liens internes",
    tabAudit: "Audit de liens",
    brokenSub: "Entrez une page (la vôtre ou une page-ressource d'un concurrent). On teste chaque lien sortant et on liste les liens morts - la base du « broken-link building » : proposez votre contenu en remplacement.",
    urlPh: "exemple.com/ressources",
    scan: "Trouver les liens cassés",
    scanning: "Vérification des liens...",
    brokenLoading: "Test de chaque lien sortant...",
    totalLinks: "Liens totaux",
    checkedLinks: "Vérifiés",
    brokenLinks: "Cassés",
    noBroken: "Aucun lien cassé sur cette page. Impeccable!",
    truncated: (n) => `Seuls les ${n} premiers liens ont été vérifiés.`,
    thStatus: "Statut",
    thType: "Type",
    thUrl: "URL cassée",
    thAnchor: "Texte d'ancre",
    internalType: "interne",
    externalType: "externe",
    internalSub: "Entrez votre propre domaine. On explore jusqu'à 20 pages, on repère les pages orphelines (aucun lien interne ne pointe vers elles) et on suggère des liens internes entre pages liées - ça aide les moteurs de recherche et d'IA à comprendre votre site.",
    yourDomain: "votresite.com",
    analyze: "Analyser les liens internes",
    analyzing: "Exploration du site...",
    internalLoading: "Exploration de jusqu'à 20 pages et cartographie des liens internes...",
    pagesCrawled: "Pages explorées",
    orphans: "Pages orphelines",
    thinPages: "Peu liées",
    orphansTitle: "Pages orphelines (aucun lien interne ne pointe ici)",
    orphansDesc: "Ces pages sont difficiles à découvrir pour les robots et les visiteurs. Ajoutez-y des liens internes depuis des pages liées.",
    thinTitle: "Pages peu liées (1 seul lien interne)",
    suggestionsTitle: "Liens internes suggérés",
    suggestionsDesc: "Pages liées qui ne se pointent pas encore l'une l'autre. Ajoutez un lien contextuel de la source vers la cible.",
    from: "De",
    to: "Vers",
    sharedTopics: "Sujets communs",
    noneInternal: "Aucune page orpheline ou peu liée - votre maillage interne semble sain.",
    mostLinked: "Pages les plus liées en interne",
    inbound: "liens entrants",
    auditSub: "Entrez une URL pour une décomposition instantanée de ses liens : interne vs externe, dofollow vs nofollow, qualité des ancres et domaines sortants.",
    audit: "Auditer les liens",
    auditing: "Analyse...",
    total: "Liens totaux",
    internal: "Internes",
    external: "Externes",
    dofollow: "Dofollow",
    nofollow: "Nofollow",
    emptyAnchors: "Ancres vides",
    genericAnchors: "Ancres génériques",
    anchorWarn: "Les ancres génériques comme « cliquez ici » transmettent moins de contexte aux moteurs de recherche et d'IA. Utilisez des ancres descriptives.",
    topDomains: "Principaux domaines externes liés",
    topAnchors: "Ancres les plus fréquentes",
    thDomain: "Domaine",
    thCount: "Liens",
    thAnchorText: "Ancre",
    externalList: "Liens externes",
    errTitle: "Analyse impossible",
    errEmpty: "Veuillez entrer une URL",
  },
};

const statusCls = (s) => (s >= 500 ? "bg-red-50 text-red-700 border-red-200" : "bg-amber-50 text-amber-700 border-amber-200");

export default function LinkTools() {
  const { lang } = useLang();
  const t = T[lang];

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-8 sm:py-10">
      <div className="max-w-2xl">
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">{t.title}</h1>
        <p className="mt-2 text-muted-foreground">{t.sub}</p>
      </div>

      <Tabs defaultValue="broken" className="mt-8">
        <TabsList>
          <TabsTrigger value="broken" data-testid="link-tab-broken"><Unlink className="h-3.5 w-3.5 mr-1.5" />{t.tabBroken}</TabsTrigger>
          <TabsTrigger value="internal" data-testid="link-tab-internal"><Network className="h-3.5 w-3.5 mr-1.5" />{t.tabInternal}</TabsTrigger>
          <TabsTrigger value="audit" data-testid="link-tab-audit"><Link2 className="h-3.5 w-3.5 mr-1.5" />{t.tabAudit}</TabsTrigger>
        </TabsList>
        <TabsContent value="broken" className="mt-6"><BrokenTab t={t} /></TabsContent>
        <TabsContent value="internal" className="mt-6"><InternalTab t={t} /></TabsContent>
        <TabsContent value="audit" className="mt-6"><AuditTab t={t} statusCls={statusCls} /></TabsContent>
      </Tabs>
    </div>
  );
}

function useTool(endpoint, timeout = 120000) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const run = async (url, emptyMsg) => {
    if (!url.trim()) { setError(emptyMsg); return; }
    setLoading(true); setError(null); setResult(null);
    try {
      const { data } = await api.post(endpoint, { url: url.trim() }, { timeout });
      setResult(data);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  return { loading, error, result, run };
}

const SearchForm = ({ value, onChange, onSubmit, placeholder, button, buttonLoading, loading, testid }) => (
  <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }} className="flex flex-col sm:flex-row gap-3 max-w-2xl">
    <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="h-11 bg-card" data-testid={`${testid}-input`} />
    <Button type="submit" size="lg" disabled={loading} className="h-11 shrink-0" data-testid={`${testid}-button`}>
      <Search className="h-4 w-4 mr-2" />{loading ? buttonLoading : button}
    </Button>
  </form>
);

const ErrorBox = ({ t, error }) => (
  <Alert variant="destructive" className="mt-6 max-w-2xl">
    <AlertTriangle className="h-4 w-4" />
    <AlertTitle>{t.errTitle}</AlertTitle>
    <AlertDescription>{error}</AlertDescription>
  </Alert>
);

const StatCard = ({ label, value, cls = "" }) => (
  <Card><CardContent className="p-5">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className={`mt-1 text-2xl font-heading font-semibold tabular-nums ${cls}`}>{value}</p>
  </CardContent></Card>
);

// --- Broken Link Finder ---
const BrokenTab = ({ t }) => {
  const [url, setUrl] = useState("");
  const { loading, error, result, run } = useTool("/links/broken", 150000);
  return (
    <div>
      <p className="text-sm text-muted-foreground max-w-2xl mb-4">{t.brokenSub}</p>
      <SearchForm value={url} onChange={setUrl} onSubmit={() => run(url, t.errEmpty)} placeholder={t.urlPh} button={t.scan} buttonLoading={t.scanning} loading={loading} testid="broken" />
      {error && <ErrorBox t={t} error={error} />}
      {loading && <div className="mt-8 space-y-4"><div className="rounded-lg border bg-card p-5 flex items-center gap-4"><Unlink className="h-6 w-6 text-primary animate-pulse" /><p className="text-sm font-medium">{t.brokenLoading}</p></div><Skeleton className="h-48 rounded-lg" /></div>}
      {result && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-8 space-y-6">
          <div className="grid grid-cols-3 gap-4 max-w-lg">
            <StatCard label={t.totalLinks} value={result.total_links} />
            <StatCard label={t.checkedLinks} value={result.checked} />
            <StatCard label={t.brokenLinks} value={result.broken_count} cls={result.broken_count > 0 ? "text-red-600" : "text-emerald-600"} />
          </div>
          {result.truncated && <p className="text-xs text-muted-foreground">{t.truncated(result.checked)}</p>}
          {result.broken_count === 0 ? (
            <Alert className="border-emerald-200 bg-emerald-50 max-w-2xl"><CheckCircle2 className="h-4 w-4 text-emerald-600" /><AlertDescription>{t.noBroken}</AlertDescription></Alert>
          ) : (
            <Card><CardContent className="px-0">
              <div className="max-h-[560px] overflow-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-card z-10"><TableRow>
                    <TableHead className="pl-6">{t.thStatus}</TableHead><TableHead>{t.thType}</TableHead><TableHead>{t.thUrl}</TableHead><TableHead className="pr-6">{t.thAnchor}</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {result.broken.map((b, i) => (
                      <TableRow key={i} className="hover:bg-muted/40">
                        <TableCell className="pl-6"><Badge variant="outline" className={`${statusCls(b.status || 500)} text-[10px]`}>{b.status || b.reason}</Badge></TableCell>
                        <TableCell><Badge variant="secondary" className="text-[10px]">{b.type === "internal" ? t.internalType : t.externalType}</Badge></TableCell>
                        <TableCell className="max-w-[340px]"><span className="text-xs break-all line-clamp-2">{b.url}</span></TableCell>
                        <TableCell className="pr-6 max-w-[180px]"><span className="text-xs text-muted-foreground line-clamp-2">{b.anchor || "-"}</span></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent></Card>
          )}
        </motion.div>
      )}
    </div>
  );
};

// --- Internal Link Optimizer ---
const InternalTab = ({ t }) => {
  const [url, setUrl] = useState("");
  const { loading, error, result, run } = useTool("/links/internal", 180000);
  return (
    <div>
      <p className="text-sm text-muted-foreground max-w-2xl mb-4">{t.internalSub}</p>
      <SearchForm value={url} onChange={setUrl} onSubmit={() => run(url, t.errEmpty)} placeholder={t.yourDomain} button={t.analyze} buttonLoading={t.analyzing} loading={loading} testid="internal" />
      {error && <ErrorBox t={t} error={error} />}
      {loading && <div className="mt-8 space-y-4"><div className="rounded-lg border bg-card p-5 flex items-center gap-4"><Network className="h-6 w-6 text-primary animate-pulse" /><p className="text-sm font-medium">{t.internalLoading}</p></div><Skeleton className="h-48 rounded-lg" /></div>}
      {result && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-8 space-y-6">
          <div className="grid grid-cols-3 gap-4 max-w-lg">
            <StatCard label={t.pagesCrawled} value={result.pages_crawled} />
            <StatCard label={t.orphans} value={result.orphan_count} cls={result.orphan_count > 0 ? "text-red-600" : "text-emerald-600"} />
            <StatCard label={t.thinPages} value={result.thin_count} cls={result.thin_count > 0 ? "text-amber-600" : ""} />
          </div>

          {result.orphan_count === 0 && result.thin_count === 0 && result.suggestions.length === 0 && (
            <Alert className="border-emerald-200 bg-emerald-50 max-w-2xl"><CheckCircle2 className="h-4 w-4 text-emerald-600" /><AlertDescription>{t.noneInternal}</AlertDescription></Alert>
          )}

          {result.orphans.length > 0 && (
            <Card><CardHeader className="pb-3"><CardTitle className="text-base font-heading">{t.orphansTitle}</CardTitle><p className="text-sm text-muted-foreground">{t.orphansDesc}</p></CardHeader>
              <CardContent><ul className="space-y-2">{result.orphans.map((o, i) => (
                <li key={i} className="text-sm flex items-start gap-2"><span className="h-1.5 w-1.5 rounded-full bg-red-500 shrink-0 mt-1.5" /><a href={o.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline break-all">{o.title || o.url}</a></li>
              ))}</ul></CardContent>
            </Card>
          )}

          {result.thin.length > 0 && (
            <Card><CardHeader className="pb-3"><CardTitle className="text-base font-heading">{t.thinTitle}</CardTitle></CardHeader>
              <CardContent><ul className="space-y-2">{result.thin.map((o, i) => (
                <li key={i} className="text-sm flex items-start gap-2"><span className="h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0 mt-1.5" /><a href={o.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline break-all">{o.title || o.url}</a></li>
              ))}</ul></CardContent>
            </Card>
          )}

          {result.suggestions.length > 0 && (
            <Card><CardHeader className="pb-3"><CardTitle className="text-base font-heading">{t.suggestionsTitle}</CardTitle><p className="text-sm text-muted-foreground">{t.suggestionsDesc}</p></CardHeader>
              <CardContent className="space-y-3">{result.suggestions.map((s, i) => (
                <div key={i} className="rounded-md border p-3 text-sm">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-muted-foreground">{t.from}:</span>
                    <a href={s.from} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">{s.from_title || s.from}</a>
                    <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">{t.to}:</span>
                    <a href={s.to} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">{s.to_title || s.to}</a>
                  </div>
                  <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                    <span className="text-xs text-muted-foreground">{t.sharedTopics}:</span>
                    {s.shared_keywords.map((k) => <Badge key={k} variant="secondary" className="text-[10px]">{k}</Badge>)}
                  </div>
                </div>
              ))}</CardContent>
            </Card>
          )}

          {result.most_linked.length > 0 && (
            <Card><CardHeader className="pb-3"><CardTitle className="text-base font-heading">{t.mostLinked}</CardTitle></CardHeader>
              <CardContent><ul className="space-y-1.5">{result.most_linked.map((m, i) => (
                <li key={i} className="text-xs flex items-center justify-between gap-3">
                  <a href={m.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline break-all line-clamp-1">{m.url}</a>
                  <Badge variant="outline" className="shrink-0">{m.inbound} {t.inbound}</Badge>
                </li>
              ))}</ul></CardContent>
            </Card>
          )}
        </motion.div>
      )}
    </div>
  );
};

// --- Link Auditor ---
const AuditTab = ({ t, statusCls }) => {
  const [url, setUrl] = useState("");
  const { loading, error, result, run } = useTool("/links/audit", 60000);
  return (
    <div>
      <p className="text-sm text-muted-foreground max-w-2xl mb-4">{t.auditSub}</p>
      <SearchForm value={url} onChange={setUrl} onSubmit={() => run(url, t.errEmpty)} placeholder="example.com/page" button={t.audit} buttonLoading={t.auditing} loading={loading} testid="audit" />
      {error && <ErrorBox t={t} error={error} />}
      {loading && <Skeleton className="mt-8 h-48 rounded-lg" />}
      {result && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-8 space-y-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            <StatCard label={t.total} value={result.total} />
            <StatCard label={t.internal} value={result.internal_count} />
            <StatCard label={t.external} value={result.external_count} />
            <StatCard label={t.dofollow} value={result.dofollow_count} />
            <StatCard label={t.nofollow} value={result.nofollow_count} />
            <StatCard label={t.genericAnchors} value={result.generic_anchor_count} cls={result.generic_anchor_count > 0 ? "text-amber-600" : ""} />
          </div>

          {result.generic_anchor_count > 0 && (
            <Alert className="border-amber-200 bg-amber-50 max-w-3xl"><AlertTriangle className="h-4 w-4 text-amber-600" /><AlertDescription>{t.anchorWarn}</AlertDescription></Alert>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {result.top_external_domains.length > 0 && (
              <Card><CardHeader className="pb-3"><CardTitle className="text-base font-heading">{t.topDomains}</CardTitle></CardHeader>
                <CardContent className="px-0"><Table><TableHeader><TableRow><TableHead className="pl-6">{t.thDomain}</TableHead><TableHead className="pr-6 text-right">{t.thCount}</TableHead></TableRow></TableHeader>
                  <TableBody>{result.top_external_domains.map((d, i) => (
                    <TableRow key={i}><TableCell className="pl-6 text-sm">{d.domain}</TableCell><TableCell className="pr-6 text-right tabular-nums text-sm">{d.count}</TableCell></TableRow>
                  ))}</TableBody></Table></CardContent>
              </Card>
            )}
            {result.top_anchors.length > 0 && (
              <Card><CardHeader className="pb-3"><CardTitle className="text-base font-heading">{t.topAnchors}</CardTitle></CardHeader>
                <CardContent className="px-0"><Table><TableHeader><TableRow><TableHead className="pl-6">{t.thAnchorText}</TableHead><TableHead className="pr-6 text-right">{t.thCount}</TableHead></TableRow></TableHeader>
                  <TableBody>{result.top_anchors.map((a, i) => (
                    <TableRow key={i}><TableCell className="pl-6 text-sm max-w-[240px]"><span className="line-clamp-1">{a.anchor}</span></TableCell><TableCell className="pr-6 text-right tabular-nums text-sm">{a.count}</TableCell></TableRow>
                  ))}</TableBody></Table></CardContent>
              </Card>
            )}
          </div>

          {result.external_links.length > 0 && (
            <Card><CardHeader className="pb-3"><CardTitle className="text-base font-heading flex items-center gap-2"><ExternalLink className="h-4 w-4" />{t.externalList} ({result.external_count})</CardTitle></CardHeader>
              <CardContent className="px-0"><div className="max-h-[400px] overflow-auto"><Table><TableBody>
                {result.external_links.map((l, i) => (
                  <TableRow key={i} className="hover:bg-muted/40">
                    <TableCell className="pl-6 max-w-[420px]"><a href={l.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline break-all line-clamp-1">{l.url}</a><span className="block text-[11px] text-muted-foreground line-clamp-1">{l.anchor || "-"}</span></TableCell>
                    <TableCell className="pr-6 text-right">{l.nofollow && <Badge variant="outline" className="text-[10px]">nofollow</Badge>}</TableCell>
                  </TableRow>
                ))}
              </TableBody></Table></div></CardContent>
            </Card>
          )}
        </motion.div>
      )}
    </div>
  );
};
